'use strict'

import * as React from 'react'

import { titleName, viewId, table } from '../../../_constants/Code'
import { errorLog } from '../../../common/logger/Logger'
import { formatDate } from '../../../common/utils/DateUtils'
import { getDynamoDB, getGroupCompany, getDataCreateTask } from '../../../common/utils/DynamoDbUtils'
import { getRefAuthInfo } from '../../../services/auth/AuthorityService'

import { useSelector } from 'react-redux'

import { fetchDataUpdateTaskInfo } from '../TaskList/extra/services'
import { isUpdateWaitingTask } from '../TaskList/extra/helpers'

/** 表の列に対応する期間種別（表示順） */
const PERIOD_KIND_KEYS = ['monthly', 'quarter', 'year']

/** 「更新済み（最新）」の候補となる表示ステータス */
const UPDATED_DISPLAY_STATUSES = ['updated', 'confirmed']

/** 更新待ちありを知らせる文言 */
const UPDATE_WAITING_MESSAGE = '更新待ちがあります'

/**
 * 「更新済み（最新）」タスクの鍵アイコンのクラス名を返す
 *
 * @param {object} task - ビューモデルのタスク
 * @returns {string} アイコンのクラス名（確定済＝グレーの鍵、未確定＝黄色の鍵）
 */
const _getLatestIcon = (task) => {
  return task.displayStatus === 'confirmed' ? 'icon-padlock-close grey2--text' : 'icon-padlock-open yellow--text'
}

/**
 * 更新済み（最新）タスクの表示テキストを組み立てる。 データ作成タスク管理のレコードから最終更新日時を突き合わせ、 「期間名（YYYY/MM/DD hh:mm）」形式で返す（日時が取得できない場合は期間名のみ）。
 *
 * @param {object} task - ビューモデルのタスク
 * @param {object[]} dataCreateTaskList - 同一会社のデータ作成タスク管理レコード
 * @returns {string} 表示テキスト
 */
const _buildLatestText = (task, dataCreateTaskList) => {
  const record = dataCreateTaskList.find((item) => Number(item.Key) === Number(task.key))
  if (!record || !record.DataCreateDateTime) {
    return task.periodValue
  }
  const fData = formatDate(new Date(record.DataCreateDateTime)).slice(0, -3)
  return task.periodValue + '（' + fData + '）'
}

/**
 * 1社分のタスクから期間種別1列分の表示データを組み立てる。 更新待ちはタスク一覧ヘッダの「更新待ち〇件」「再更新待ち〇件」と同一基準 （isUpdateWaitingTask）で判定し、Key昇順（古い順）に列挙する。
 * 更新済み（最新）は更新済・確定済のうち更新待ちに該当しないKey最大の1件とする。
 *
 * @param {object[]} tasks - 1社分の全タスク（ビューモデル）
 * @param {string} kind - 期間種別（monthly / quarter / year）
 * @param {object[]} dataCreateTaskList - 同一会社のデータ作成タスク管理レコード（最終更新日時の表示用）
 * @returns {{ latestText: string; latestIcon: string; waitingTexts: string[] }} 列表示データ
 */
const _buildPeriodColumn = (tasks, kind, dataCreateTaskList) => {
  const kindTasks = tasks.filter((task) => task.periodKind === kind).sort((a, b) => Number(a.key) - Number(b.key))
  const waitingTasks = kindTasks.filter((task) => isUpdateWaitingTask(task))
  const updatedTasks = kindTasks.filter(
    (task) => UPDATED_DISPLAY_STATUSES.includes(task.displayStatus) && !isUpdateWaitingTask(task)
  )
  const latestTask = updatedTasks.length > 0 ? updatedTasks[updatedTasks.length - 1] : null
  return {
    latestText: latestTask ? _buildLatestText(latestTask, dataCreateTaskList) : '',
    latestIcon: latestTask ? _getLatestIcon(latestTask) : '',
    waitingTexts: waitingTasks.map((task) => task.periodValue)
  }
}

/**
 * データ更新状況モーダル（TOP画面「詳細」ボタンの子画面）の本体。 グループ各社のデータ更新タスク情報（IF02098）から、期間種別ごとに 「更新済み（最新）」と「更新待ち」を表示する。
 *
 * @param {{ corporationNumber: string; isParent: boolean; userId: string }} props
 */
const DataUpdateStatusContent = (props) => {
  const [isLoaded, setIsLoaded] = React.useState(false)
  const [updateStatusList, setUpdateStatusList] = React.useState([])

  React.useEffect(() => {
    init()
  }, [])

  /** 初期表示。表示対象の会社一覧を組み立て、各社のデータ更新タスク情報を 並列取得して期間種別ごとの表示データを構築する。 */
  const init = async () => {
    const recordList = []
    try {
      // 会社情報取得
      const companyData = await getDynamoDB(table.Company, null, props.corporationNumber, null, null)
      recordList.push({
        corporationNumber: companyData[0].CorporationNumber,
        name: companyData[0].CompName
      })
      // 連結会社&セグメント取得
      const analysisTargetData = await getDynamoDB(table.AnalysisTargetInfo, null, props.corporationNumber)
      // 親会社判定
      if (analysisTargetData.length) {
        // 参照権限のある会社
        const compNumberList = await _getAuthCompanyNumber(props.userId, props.corporationNumber)
        // 表示対象グループ会社情報抽出
        analysisTargetData[0].Corporation.forEach((groups) => {
          // グループ会社一覧
          groups.CorporationList.forEach((corp) => {
            if (
              compNumberList.includes(corp.Number) &&
              recordList.every((rec) => rec.corporationNumber !== corp.Number)
            ) {
              recordList.push({
                corporationNumber: corp.Number,
                name: corp.Name
              })
            }
          })
        })
      }
      // 各社のデータ更新タスク情報（IF02098）とデータ作成タスク管理（最終更新日時用）を並列取得
      const fetchedList = await Promise.all(
        recordList.map((rec) =>
          Promise.all([_fetchTaskInfo(rec.corporationNumber), _fetchDataCreateTask(rec.corporationNumber)])
        )
      )
      // 会社×期間種別ごとの表示データを構築
      recordList.forEach((rec, idx) => {
        const [taskInfo, dataCreateTaskList] = fetchedList[idx]
        const tasks = taskInfo.fiscalPeriodGroups.flatMap((group) => group.tasks)
        PERIOD_KIND_KEYS.forEach((kind) => {
          rec[kind] = _buildPeriodColumn(tasks, kind, dataCreateTaskList)
        })
        rec.hasWaiting = PERIOD_KIND_KEYS.some((kind) => rec[kind].waitingTexts.length > 0)
      })
      setUpdateStatusList(recordList)
      setIsLoaded(true)
    } catch (error) {
      errorLog('E000001', props.userId, viewId.dataUpdateStatus, 'init', error)
    }
  }

  /**
   * 表示対象グループ会社抽出
   *
   * @param {String} userId ユーザーID
   * @param {String} corporationNumber 法人番号
   * @returns 法人番号配列
   */
  const _getAuthCompanyNumber = async (userId, corporationNumber) => {
    try {
      // グループ会社を取得
      const groupCompanyMaster = await getGroupCompany(corporationNumber)
      // ユーザー情報取得
      const userData = await getDynamoDB(table.User, null, corporationNumber, { UserId: userId })
      // 権限情報取得
      const authInfo = await getRefAuthInfo(userData[0], groupCompanyMaster)
      return authInfo.authCompany
    } catch (error) {
      errorLog('E000001', props.userId, viewId.dataUpdateStatus, '_getAuthCompanyNumber', error)
    }
  }

  /**
   * 1社分のデータ更新タスク情報（IF02098）を取得する。 取得に失敗した会社は空のタスク情報として扱い、他社の表示は継続する。
   *
   * @param {String} corporationNumber 法人番号
   * @returns {Promise<{ fiscalPeriodOptions: string[]; fiscalPeriodGroups: object[] }>} タスク情報
   */
  const _fetchTaskInfo = async (corporationNumber) => {
    try {
      return await fetchDataUpdateTaskInfo(corporationNumber)
    } catch (error) {
      errorLog('E000001', props.userId, viewId.dataUpdateStatus, '_fetchTaskInfo', error)
      return { fiscalPeriodOptions: [], fiscalPeriodGroups: [] }
    }
  }

  /**
   * 1社分のデータ作成タスク管理を取得する（更新済み（最新）の最終更新日時表示用）。 取得に失敗した会社は日時なし（期間名のみ）で表示を継続する。
   *
   * @param {String} searchKey 法人番号
   * @returns {Promise<object[]>} データ作成タスク管理レコード
   */
  const _fetchDataCreateTask = async (searchKey) => {
    try {
      const dataCreateTask = await getDataCreateTask(props.corporationNumber, searchKey)
      return Array.isArray(dataCreateTask) ? dataCreateTask : []
    } catch (error) {
      errorLog('E000001', props.userId, viewId.dataUpdateStatus, '_fetchDataCreateTask', error)
      return []
    }
  }

  /** データ更新状況確認モーダルを開く */
  const _openModal = () => {
    const modalBg = document.querySelector('.bg-modal-window')
    modalBg.classList.add('is-active')
    const modalUploading = document.querySelector('#modal-window--data-update-status')
    if (!modalUploading.classList.contains('animate__fadeIn')) {
      modalUploading.classList.remove('animate__fadeOut')
      modalUploading.classList.add('animate__fadeIn')
    }
    modalUploading.classList.remove('animate__fadeOut')
    modalUploading.classList.add('animate__fadeIn')
    modalUploading.classList.add('is-active')
  }

  /** データ更新状況確認モーダルを閉じる */
  const _closeModal = () => {
    const modal = document.querySelector('#modal-window--data-update-status')
    if (!modal.classList.contains('animate__fadeOut')) {
      modal.classList.remove('animate__fadeIn')
      modal.classList.add('animate__fadeOut')
    }
    const fadeOutTimeoutSec = 300
    setTimeout(function () {
      document.querySelector('.bg-modal-window.is-active').classList.remove('is-active')
      document.querySelector('.modal-window.is-active').classList.remove('is-active')
    }, fadeOutTimeoutSec)
  }

  /** 描画処理 */
  if (!isLoaded) {
    return null
  }
  // 参照権限のあるいずれかの会社に更新待ちタスクがあるか（子画面の表示内容と同一基準）
  const hasAnyWaiting = updateStatusList.some((rec) => rec.hasWaiting)
  return (
    <React.Fragment>
      <button
        type="button"
        className="btn-modal-data-update-status btn link rounded-lg px-6 py-2.5 white--text text-h7 ml-4"
        onClick={() => _openModal()}
      >
        詳細
      </button>
      {hasAnyWaiting && (
        <span className="inline-flex items-center text-nowrap align-middle ml-2">
          <span className="icon-warning red--text text-3xl"></span>
          <span className="red--text font-medium ml-1">{UPDATE_WAITING_MESSAGE}</span>
        </span>
      )}
      <div className="bg-modal-window w-screen h-screen fixed top-0 left-0 grey lighten-70 z-50"></div>
      <div
        id="modal-window--data-update-status"
        className="modal-window modal-window__wide is-w1456 fixed bg sub z-50 rounded-2xl animate__fadeIn"
      >
        <div className="modal-window__header">
          <div className="modal-window__title flex items-center justify-center py-10">
            <span className="modal-window__title-text text-h3 font-medium">{titleName.dataUpdateStatus}</span>
          </div>
          <button
            type="button"
            className="modal-window__btn-close absolute top-5 right-5"
            onClick={() => _closeModal()}
          >
            <span className="icon-close"></span>
          </button>
        </div>
        <div className="modal-window__content px-8 pb-10">
          <div className="text-caption-1 font-medium text-center mb-8 link--text">
            {props.isParent && 'グループ各社の'}データ更新状況を表示します。
            <br />
            <span className="icon-padlock-open red--text text-2xl align-sub"></span>：更新待ちデータ、
            <span className="icon-padlock-open yellow--text text-2xl align-sub"></span>：未確定データ、
            <span className="icon-padlock-close grey2--text text-2xl align-sub"></span>：確定データ
            <br />
            カッコ内はデータの最終更新日時です。
          </div>
          <div className="setting-table is-data-update-status">
            <table className="setting-table__table w-full">
              <thead>
                <tr>
                  <th className="setting-table__col-name">会社名</th>
                  <th></th>
                  <th className="setting-table__col-monthly">月次</th>
                  <th className="setting-table__col-quarter">四半期</th>
                  <th className="setting-table__col-annual">年次</th>
                </tr>
              </thead>
              <tbody className="text-caption-1">
                {updateStatusList.map((rec) => {
                  return (
                    <React.Fragment key={rec.corporationNumber}>
                      <tr>
                        <th
                          rowSpan={rec.hasWaiting ? 2 : 1}
                          className="text-caption-1 font-bold blue1 lighten-7 link--text"
                        >
                          {rec.name}
                        </th>
                        <th className="text-caption-1 font-medium text-nowrap">更新済み（最新）</th>
                        {PERIOD_KIND_KEYS.map((kind) => (
                          <td key={kind} className="text-caption-1 left text-nowrap">
                            {rec[kind].latestText && (
                              <React.Fragment>
                                <span className={`text-2xl align-sub mr-2 ${rec[kind].latestIcon}`}></span>
                                {rec[kind].latestText}
                              </React.Fragment>
                            )}
                          </td>
                        ))}
                      </tr>
                      {rec.hasWaiting && (
                        <tr>
                          <th className="text-caption-1 font-medium text-nowrap red--text">更新待ち</th>
                          {PERIOD_KIND_KEYS.map((kind) => (
                            <td key={kind} className="text-caption-1 left align-top">
                              {rec[kind].waitingTexts.map((text) => (
                                <div key={text} className="text-nowrap">
                                  <span className="icon-padlock-open red--text text-2xl align-sub mr-2"></span>
                                  {text}
                                </div>
                              ))}
                            </td>
                          ))}
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="text-center mt-8">
            <button
              type="button"
              className="btn btn-modal-data-update-status-ok white--text grey rounded-lg px-16 text-h5 font-medium"
              onClick={() => _closeModal()}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

/** データ更新状況モーダルのエントリコンポーネント。 セッションから法人番号・親会社フラグ・ユーザーIDを取得して本体へ渡す。 */
const DataUpdateStatus = () => {
  const state = useSelector((state) => state)

  return (
    <DataUpdateStatusContent
      corporationNumber={state.company.corporationNumber}
      isParent={state.company.isParent}
      userId={state.user.sub}
    />
  )
}

export default DataUpdateStatus
